import { toggleFocusMode } from '@src/store/slices/focus_mode';
import { hasInputLikeFocus } from '@src/utils/dom/inputElement';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/**
 * キーボードショートカットの定義
 */
interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  action: () => void;
  description: string;
}

/**
 * グローバルキーボードショートカットフック
 *
 * アプリケーション全体で使用できるキーボードショートカットを提供します。
 * WCAG 2.1のガイドラインに準拠し、アクセシビリティを向上させます。
 *
 * @returns {void}
 */
export const useKeyboardShortcuts = (): void => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * ヘルプモーダルの表示
   */
  const showHelp = useCallback(() => {
    // ヘルプモーダルの表示ロジック
    const helpMessage = `
キーボードショートカット一覧:
━━━━━━━━━━━━━━━━━━
• ? または Shift+/ : ヘルプを表示
• Ctrl+S : 現在のフォームを保存
• Ctrl+F : フォーカスモードの切替
• Alt+H : ホームへ移動
• Alt+← : 前のページへ
• Alt+→ : 次のページへ
• Escape : モーダル/メニューを閉じる
• Tab : 次の要素へフォーカス移動
• Shift+Tab : 前の要素へフォーカス移動
━━━━━━━━━━━━━━━━━━
    `.trim();

    // アラートで表示（後でモーダルコンポーネントに置き換え可能）
    alert(helpMessage);
  }, []);

  /**
   * フォームの保存処理
   */
  const saveForm = useCallback(() => {
    // 保存ボタンを探してクリック
    const saveButton = document.querySelector(
      '[type="submit"], button[aria-label*="保存"]'
    ) as HTMLButtonElement;
    if (saveButton && !saveButton.disabled) {
      saveButton.click();

      // スクリーンリーダー用の通知
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.textContent = 'フォームを保存しています...';
      announcement.style.position = 'absolute';
      announcement.style.left = '-10000px';
      document.body.appendChild(announcement);

      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 3000);
    }
  }, []);

  /**
   * フォーカスモードの切替
   */
  const toggleFocus = useCallback(() => {
    dispatch(toggleFocusMode());

    // スクリーンリーダー用の通知
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.textContent = 'フォーカスモードを切り替えました';
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 3000);
  }, [dispatch]);

  /**
   * ナビゲーション処理
   */
  const navigateHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const navigatePrev = useCallback(() => {
    const prevButton = document.querySelector('[aria-label="前のページ"]') as HTMLButtonElement;
    if (prevButton && !prevButton.disabled) {
      prevButton.click();
    }
  }, []);

  const navigateNext = useCallback(() => {
    const nextButton = document.querySelector('[aria-label="次のページ"]') as HTMLButtonElement;
    if (nextButton && !nextButton.disabled) {
      nextButton.click();
    }
  }, []);

  /**
   * ESCキーでモーダルやメニューを閉じる
   */
  const closeModal = useCallback(() => {
    // MUIのモーダル/メニューを閉じる
    const closeButton = document.querySelector(
      '[aria-label*="閉じる"], [aria-label*="close"]'
    ) as HTMLButtonElement;
    if (closeButton) {
      closeButton.click();
      return;
    }

    // バックドロップをクリックしてモーダルを閉じる
    const backdrop = document.querySelector('.MuiBackdrop-root') as HTMLElement;
    if (backdrop) {
      backdrop.click();
    }
  }, []);

  /**
   * キーボードショートカットの定義
   */
  const shortcuts: KeyboardShortcut[] = useMemo(
    () => [
      {
        key: '?',
        shiftKey: true,
        action: showHelp,
        description: 'ヘルプを表示',
      },
      {
        key: 's',
        ctrlKey: true,
        action: saveForm,
        description: 'フォームを保存',
      },
      {
        key: 'f',
        ctrlKey: true,
        action: toggleFocus,
        description: 'フォーカスモードの切替',
      },
      {
        key: 'h',
        altKey: true,
        action: navigateHome,
        description: 'ホームへ移動',
      },
      {
        key: 'ArrowLeft',
        altKey: true,
        action: navigatePrev,
        description: '前のページへ',
      },
      {
        key: 'ArrowRight',
        altKey: true,
        action: navigateNext,
        description: '次のページへ',
      },
      {
        key: 'Escape',
        action: closeModal,
        description: 'モーダル/メニューを閉じる',
      },
    ],
    [showHelp, saveForm, toggleFocus, navigateHome, navigatePrev, navigateNext, closeModal]
  );

  /**
   * キーボードイベントハンドラー
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // 入力フィールドやテキストエリアにフォーカスがある場合は、
      // 特定のショートカットのみ動作させる
      const isInputFocused = hasInputLikeFocus();

      for (const shortcut of shortcuts) {
        const keyMatch =
          event.key &&
          shortcut.key &&
          (event.key === shortcut.key || event.key.toLowerCase() === shortcut.key.toLowerCase());
        const ctrlMatch = !shortcut.ctrlKey || event.ctrlKey || event.metaKey;
        const shiftMatch = !shortcut.shiftKey || event.shiftKey;
        const altMatch = !shortcut.altKey || event.altKey;
        const metaMatch = !shortcut.metaKey || event.metaKey;

        if (keyMatch && ctrlMatch && shiftMatch && altMatch && metaMatch) {
          // 入力フィールドにフォーカスがある場合、
          // Ctrl+S、Escape、ヘルプのみ動作させる
          if (isInputFocused) {
            if (
              shortcut.key === 'Escape' ||
              (shortcut.key === 's' && shortcut.ctrlKey) ||
              (shortcut.key === '?' && shortcut.shiftKey)
            ) {
              event.preventDefault();
              shortcut.action();
            }
          } else {
            event.preventDefault();
            shortcut.action();
          }
          break;
        }
      }
    },
    [shortcuts]
  );

  /**
   * イベントリスナーの登録
   */
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};

/**
 * キーボードショートカットの一覧を取得
 *
 * @returns {Array<{ keys: string; description: string }>} ショートカット一覧
 */
const _getKeyboardShortcuts = (): Array<{ keys: string; description: string }> => {
  return [
    { keys: '? (Shift+/)', description: 'ヘルプを表示' },
    { keys: 'Ctrl+S', description: '現在のフォームを保存' },
    { keys: 'Ctrl+F', description: 'フォーカスモードの切替' },
    { keys: 'Alt+H', description: 'ホームへ移動' },
    { keys: 'Alt+←', description: '前のページへ' },
    { keys: 'Alt+→', description: '次のページへ' },
    { keys: 'Escape', description: 'モーダル/メニューを閉じる' },
    { keys: 'Tab', description: '次の要素へフォーカス移動' },
    { keys: 'Shift+Tab', description: '前の要素へフォーカス移動' },
  ];
};
